import { saveAs } from 'file-saver';
import ExcelJS from 'exceljs';
import type { Requirement } from '@/types';

// BSP需求字段映射（原始字段名 -> Excel标题）
const fieldMap = {
  reqId: "需求编号",
  owner: "责任人",
  description: "描述",
  deliveryStage: "交付阶段",
  bspTag: "TAG",
  bspTag1: "TAG1",
  bspTag2: "TAG2",
  deliverables: "交付物",
  verificationMethod: "验证方法",
  validationCriteria: "验证准则",
  subcategory: "需求分类",
  priority: "优先级",
  status: "状态",
  remark: "备注"
};

/**
 * 按子类别分组需求
 */
function groupBySubcategory(requirements: Requirement[]): Record<string, Requirement[]> {
  const grouped: Record<string, Requirement[]> = {};

  requirements.forEach(req => {
    const subcategory = req.subcategory;
    if (!grouped[subcategory]) {
      grouped[subcategory] = [];
    }
    grouped[subcategory].push(req);
  });

  // 过滤掉空分组
  return Object.fromEntries(
    Object.entries(grouped).filter(([_, data]) => data.length > 0)
  );
}

/**
 * 主导出函数 - 基于模板的多Sheet导出
 */
export async function exportRequirementsToExcel(
  requirements: Requirement[],
  projectName: string
): Promise<void> {
  try {
    // 1. 加载模板文件
    const templateFile = await loadTemplateFile();
    if (!templateFile) {
      throw new Error('无法加载模板文件，请确保 /public/template.xlsx 存在');
    }

    // 2. 使用exceljs加载模板
    const templateBuffer = await templateFile.arrayBuffer();
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(templateBuffer);

    // 3. 按子类别分组需求
    const groupedData = groupBySubcategory(requirements);

    // 4. 填充数据到各Sheet
    await processTemplateWithData(workbook, groupedData);

    // 5. 生成Excel并下载
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    const today = new Date().toISOString().slice(0, 10);
    const fileName = `${projectName}_需求列表_${today}.xlsx`;
    saveAs(blob, fileName);
  } catch (error) {
    console.error('导出失败:', error);
    throw error;
  }
}

/**
 * 从public目录加载模板文件
 */
async function loadTemplateFile(): Promise<File | null> {
  try {
    const response = await fetch('/template.xlsx');
    if (!response.ok) {
      throw new Error(`模板文件不存在: ${response.status}`);
    }

    const blob = await response.blob();
    const file = new File([blob], 'template.xlsx', {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    return file;
  } catch (error) {
    console.error('读取模板文件失败:', error);
    return null;
  }
}

/**
 * 处理模板数据填充
 */
async function processTemplateWithData(
  workbook: ExcelJS.Workbook,
  groupedData: Record<string, Requirement[]>
): Promise<void> {
  for (const [subcategory, data] of Object.entries(groupedData)) {
    if (data.length === 0) continue;

    // 首先使用英文key查找Sheet（模板中的Sheet名称是英文的，如"Mcu", "Communication"）
    let targetSheet = workbook.getWorksheet(subcategory);

    // 如果找不到，尝试模糊匹配（不区分大小写）
    if (!targetSheet) {
      targetSheet = workbook.worksheets.find(sheet =>
        sheet.name.toLowerCase() === subcategory.toLowerCase()
      );
    }

    // 如果还是找不到Sheet，使用第一个Sheet作为模板创建新Sheet（使用英文名称）
    if (!targetSheet && workbook.worksheets[0]) {
      targetSheet = workbook.addWorksheet(subcategory);
      await copySheetStructure(workbook.worksheets[0], targetSheet);
    }

    if (targetSheet) {
      await fillSheetWithData(targetSheet, data);
    }
  }

  // 删除模板中没有数据的空Sheet
  // 注意：保留"ChangeHistory"和"模板变更记录"这两个特殊Sheet
  const sheetsToRemove: number[] = [];
  const specialSheets = ['ChangeHistory', '模板变更记录'];

  workbook.worksheets.forEach((sheet) => {
    // 跳过特殊Sheet
    if (specialSheets.includes(sheet.name)) {
      return;
    }

    // 删除没有数据的Sheet（行数<=1，即只有标题行或完全为空）
    if (sheet.rowCount <= 1 || sheet.actualRowCount <= 1) {
      sheetsToRemove.push(sheet.id);
    }
  });

  sheetsToRemove.forEach(sheetId => {
    try {
      workbook.removeWorksheet(sheetId);
    } catch (e) {
      // 忽略删除失败的情况
      console.warn('无法删除Sheet:', e);
    }
  });
}

/**
 * 复制Sheet结构（当模板中不存在某个子类别Sheet时）
 */
async function copySheetStructure(
  sourceSheet: ExcelJS.Worksheet,
  targetSheet: ExcelJS.Worksheet
): Promise<void> {
  // 复制列宽
  sourceSheet.columns.forEach((col, index) => {
    if (col.width) {
      targetSheet.getColumn(index + 1).width = col.width;
    }
  });

  // 复制前几行（通常是标题行和样式行）
  for (let rowNum = 1; rowNum <= Math.min(5, sourceSheet.rowCount); rowNum++) {
    const sourceRow = sourceSheet.getRow(rowNum);
    const targetRow = targetSheet.getRow(rowNum);

    sourceRow.eachCell((cell, colNumber) => {
      const targetCell = targetRow.getCell(colNumber);
      targetCell.value = cell.value;
      targetCell.style = cell.style ? { ...cell.style } : getCellStyle();
    });

    if (sourceRow.height) {
      targetRow.height = sourceRow.height;
    }
  }
}

/**
 * 填充Sheet数据
 */
async function fillSheetWithData(
  sheet: ExcelJS.Worksheet,
  data: Requirement[]
): Promise<void> {
  if (data.length === 0) return;

  let headerRow: ExcelJS.Row | null = null;
  let headerRowIndex = 1;

  // 查找包含字段标题的行（搜索前5行）
  for (let i = 1; i <= Math.min(5, sheet.rowCount); i++) {
    const row = sheet.getRow(i);
    let hasFieldContent = false;

    row.eachCell((cell) => {
      if (cell.value && typeof cell.value === 'string') {
        const cellText = cell.value.trim();
        Object.values(fieldMap).forEach(fieldTitle => {
          if (cellText === fieldTitle || cellText.includes(fieldTitle) || fieldTitle.includes(cellText)) {
            hasFieldContent = true;
          }
        });
      }
    });

    if (hasFieldContent) {
      headerRow = row;
      headerRowIndex = i;
      break;
    }
  }

  // 如果没找到标题行，使用第1行作为标题行
  if (!headerRow) {
    headerRow = sheet.getRow(1);
    headerRowIndex = 1;
    Object.values(fieldMap).forEach((header, index) => {
      headerRow!.getCell(index + 1).value = header;
    });
  }

  const columnMapping = createColumnMapping(headerRow, fieldMap);
  const dataStartRow = headerRowIndex + 1;

  // 清除现有数据行
  for (let i = dataStartRow; i <= sheet.rowCount; i++) {
    sheet.spliceRows(dataStartRow, 1);
  }

  // 填充新数据
  data.forEach((item, index) => {
    const row = sheet.getRow(dataStartRow + index);

    Object.entries(fieldMap).forEach(([fieldKey, fieldTitle]) => {
      const columnIndex = columnMapping[fieldTitle];
      if (columnIndex) {
        const cell = row.getCell(columnIndex);
        cell.value = (item.hasOwnProperty(fieldKey as keyof Requirement) &&
                     item[fieldKey as keyof Requirement] !== null &&
                     item[fieldKey as keyof Requirement] !== undefined)
                     ? item[fieldKey as keyof Requirement] as string | number : '';
        cell.style = getCellStyle();
      }
    });

    // 确保所有单元格都有边框
    if (headerRow) {
      headerRow.eachCell((_, colNumber) => {
        const cell = row.getCell(colNumber);
        if (!cell.style || !cell.style.border) {
          cell.style = getCellStyle();
        }
      });
    }
  });
}

/**
 * 获取统一的单元格样式
 */
function getCellStyle() {
  return {
    font: { name: '宋体', size: 11 },
    alignment: { vertical: 'top' as const, horizontal: 'left' as const, wrapText: true },
    border: {
      top: { style: 'thin' as const, color: { argb: 'FF000000' } },
      bottom: { style: 'thin' as const, color: { argb: 'FF000000' } },
      left: { style: 'thin' as const, color: { argb: 'FF000000' } },
      right: { style: 'thin' as const, color: { argb: 'FF000000' } }
    }
  };
}

/**
 * 创建列映射关系（字段标题 -> 列号）
 */
function createColumnMapping(
  headerRow: ExcelJS.Row,
  fieldMap: Record<string, string>
): Record<string, number> {
  const mapping: Record<string, number> = {};

  // 首先进行精确匹配
  headerRow.eachCell((cell, colNumber) => {
    if (cell.value && typeof cell.value === 'string') {
      const headerText = cell.value.trim();
      Object.values(fieldMap).forEach(fieldTitle => {
        if (headerText === fieldTitle && !mapping[fieldTitle]) {
          mapping[fieldTitle] = colNumber;
        }
      });
    }
  });

  // 对于没有精确匹配的字段，进行包含匹配
  headerRow.eachCell((cell, colNumber) => {
    if (cell.value && typeof cell.value === 'string') {
      const headerText = cell.value.trim();
      Object.values(fieldMap).forEach(fieldTitle => {
        // 避免"备注"被误匹配到包含备注的其他字段
        if (!mapping[fieldTitle] && fieldTitle !== "备注") {
          if (headerText.includes(fieldTitle) || fieldTitle.includes(headerText)) {
            mapping[fieldTitle] = colNumber;
          }
        }
      });
    }
  });

  return mapping;
}
